import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { SortableHeader, useSortableData } from '@/shared/components/ui/sortable-table';
import {
  Users, UserPlus, Search, Filter, MoreHorizontal, Mail, Phone, Building2,
  MapPin, Calendar, Briefcase, RefreshCw, ChevronDown, Eye, Edit, UserX } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  terminated: 'bg-red-500/20 text-red-400 border-red-500/30',
  on_leave: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

export const EmployeesPage = () => {const { t } = useTranslation("hr");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [formData, setFormData] = useState({
    employee_no: '', first_name: '', last_name: '', email_work: '',
    phone_work: '', job_title: '', department_id: '', hire_date: '',
    employee_type: 'full_time'
  });

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/employees?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (departmentFilter !== 'all') url += `department_id=${departmentFilter}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch employees');
      const data = await res.json();
      setEmployees(data.employees || []);
    } catch (err) {
      toast.error(err.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/core/departments`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDepartments(data.departments || []);
      }
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  useEffect(() => {fetchEmployees();fetchDepartments();}, [statusFilter, departmentFilter]);
  useEffect(() => {const timer = setTimeout(fetchEmployees, 300);return () => clearTimeout(timer);}, [search]);

  // Apply sorting
  const { sortedData, sortConfig, requestSort } = useSortableData(employees);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employees`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create employee');
      }
      toast.success('Employee created successfully');
      setShowCreate(false);
      setFormData({ employee_no: '', first_name: '', last_name: '', email_work: '', phone_work: '', job_title: '', department_id: '', hire_date: '', employee_type: 'full_time' });
      fetchEmployees();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employees/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch employee details');
      const data = await res.json();
      setShowDetails(data);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const terminateEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to terminate this employee?')) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/employees/${id}/terminate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: 'Terminated via UI' })
      });
      if (!res.ok) throw new Error('Failed to terminate employee');
      toast.success('Employee terminated');
      fetchEmployees();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("employees", "Employees")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageEmployeeRecords", "Manage employee records and profiles")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <UserPlus className="h-4 w-4" />{t("addEmployee", "Add Employee")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10" />
              
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="active">{t("active", "Active")}</SelectItem>
                <SelectItem value="inactive">{t("inactive", "Inactive")}</SelectItem>
                <SelectItem value="on_leave">{t("onLeave", "On Leave")}</SelectItem>
                <SelectItem value="terminated">{t("terminated", "Terminated")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allDepartments", "All Departments")}</SelectItem>
                {departments.map((d) =>
                <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchEmployees} className="gap-2">
              <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employee List */}
      <Card>
        <CardContent className="p-0">
          {loading ?
          <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
            </div> :
          employees.length === 0 ?
          <div className="text-center py-16">
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noEmployeesFound", "No employees found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <UserPlus className="h-4 w-4 mr-2" />{t("addFirstEmployee", "Add First Employee")}
            </Button>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <SortableHeader column="full_name" label="Employee" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="department" label="Department" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="job_title" label="Job Title" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="status" label="Status" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="hire_date" label="Hire Date" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="text-right p-4 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {sortedData.map((emp) =>
                <tr key={emp.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <span className="text-green-500 font-semibold">
                              {emp.first_name?.[0]}{emp.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{emp.full_name || `${emp.first_name} ${emp.last_name}`}</p>
                            <p className="text-sm text-muted-foreground">{emp.employee_no}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span>{emp.department_name || 'Unassigned'}</span>
                        </div>
                      </td>
                      <td className="p-4">{emp.job_title || '-'}</td>
                      <td className="p-4">
                        <Badge className={statusColors[emp.status] || statusColors.inactive}>
                          {emp.status?.replace('_', ' ') || 'Unknown'}
                        </Badge>
                      </td>
                      <td className="p-4">{emp.hire_date ? new Date(emp.hire_date).toLocaleDateString() : '-'}</td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(emp.id)}>
                              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                        </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toast.info('Edit feature coming soon')}>
                              <Edit className="h-4 w-4 mr-2" />{t("edit", "Edit")}
                        </DropdownMenuItem>
                            {emp.status === 'active' &&
                        <DropdownMenuItem onClick={() => terminateEmployee(emp.id)} className="text-red-400">
                                <UserX className="h-4 w-4 mr-2" />{t("terminate", "Terminate")}
                        </DropdownMenuItem>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Employee Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("addNewEmployee", "Add New Employee")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("employeeNo", "Employee No *")}</Label>
                <Input value={formData.employee_no} onChange={(e) => setFormData({ ...formData, employee_no: e.target.value })} placeholder="EMP001" />
              </div>
              <div>
                <Label>{t("hireDate", "Hire Date *")}</Label>
                <Input type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("firstName", "First Name *")}</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
              </div>
              <div>
                <Label>{t("lastName", "Last Name *")}</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("workEmail", "Work Email")}</Label>
                <Input type="email" value={formData.email_work} onChange={(e) => setFormData({ ...formData, email_work: e.target.value })} />
              </div>
              <div>
                <Label>{t("workPhone", "Work Phone")}</Label>
                <Input value={formData.phone_work} onChange={(e) => setFormData({ ...formData, phone_work: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("jobTitle", "Job Title")}</Label>
              <Input value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("department", "Department")}</Label>
                <Select value={formData.department_id} onValueChange={(v) => setFormData({ ...formData, department_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) =>
                    <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("employeeType", "Employee Type")}</Label>
                <Select value={formData.employee_type} onValueChange={(v) => setFormData({ ...formData, employee_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">{t("fullTime", "Full Time")}</SelectItem>
                    <SelectItem value="part_time">{t("partTime", "Part Time")}</SelectItem>
                    <SelectItem value="contractor">{t("contractor", "Contractor")}</SelectItem>
                    <SelectItem value="intern">{t("intern", "Intern")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createEmployee", "Create Employee")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("employeeDetails", "Employee Details")}</DialogTitle>
          </DialogHeader>
          {showDetails &&
          <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-2xl text-green-500 font-semibold">
                    {showDetails.first_name?.[0]}{showDetails.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{showDetails.full_name}</h3>
                  <p className="text-muted-foreground">{showDetails.employee_no}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Briefcase className="h-4 w-4" />{t("jobTitle", "Job Title")}</p>
                  <p className="font-medium">{showDetails.job_title || 'Not specified'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Building2 className="h-4 w-4" />{t("department", "Department")}</p>
                  <p className="font-medium">{showDetails.department_name || 'Unassigned'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Mail className="h-4 w-4" />{t("email", "Email")}</p>
                  <p className="font-medium">{showDetails.email_work || 'Not provided'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Phone className="h-4 w-4" />{t("phone", "Phone")}</p>
                  <p className="font-medium">{showDetails.phone_work || 'Not provided'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><Calendar className="h-4 w-4" />{t("hireDate", "Hire Date")}</p>
                  <p className="font-medium">{showDetails.hire_date ? new Date(showDetails.hire_date).toLocaleDateString() : 'Not specified'}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground flex items-center gap-2"><MapPin className="h-4 w-4" />{t("site", "Site")}</p>
                  <p className="font-medium">{showDetails.site_name || 'Not assigned'}</p>
                </div>
              </div>
              {showDetails.contract &&
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />{t("activeContract", "Active Contract")}
              </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t("type", "Type")}</p>
                      <p>{showDetails.contract.contract_type}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("salary", "Salary")}</p>
                      <p>${showDetails.contract.base_salary?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("startDate", "Start Date")}</p>
                      <p>{showDetails.contract.start_date}</p>
                    </div>
                  </div>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default EmployeesPage;